/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Nav icons for the v2 shell. Routes, the module registry and users' saved
 * sidebar preferences store Material icon names ("table_view"); this maps
 * them to Lucide (CLAUDE.md: lucide-react only for new UI) without touching
 * any stored data. Unknown names fall back to the route's original icon.
 */

import { isValidElement } from "react";
import {
  Activity,
  AppWindow,
  BadgeCheck,
  BarChart3,
  Bell,
  BookOpen,
  Bot,
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  CheckSquare,
  Circle,
  ClipboardList,
  CreditCard,
  FileSpreadsheet,
  FileText,
  Gauge,
  Gift,
  Globe,
  GraduationCap,
  Grid2x2,
  Handshake,
  Headset,
  HeartPulse,
  HelpCircle,
  Home,
  Hotel,
  Inbox,
  KeyRound,
  Kanban,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  List,
  ListChecks,
  Luggage,
  Mail,
  Map,
  MapPin,
  MessageSquare,
  Network,
  Package,
  Phone,
  Plane,
  Puzzle,
  QrCode,
  Receipt,
  Route,
  Search,
  Settings,
  Shield,
  ShoppingCart,
  Sparkles,
  Star,
  Store,
  Tag,
  Target,
  Ticket,
  TrendingUp,
  UserCog,
  UserPlus,
  Users,
  Wallet,
  Waves,
  Wrench,
} from "lucide-react";

const MAP = {
  "360": Activity,
  account_balance: Building2,
  account_balance_wallet: Wallet,
  add_circle: Circle,
  admin_panel_settings: Shield,
  alt_route: Route,
  alternate_email: Mail,
  apartment: Building2,
  apps: LayoutGrid,
  article: FileText,
  assessment: BarChart3,
  assignment: ClipboardList,
  assignment_ind: UserCog,
  assignment_turned_in: ListChecks,
  auto_awesome: Sparkles,
  auto_fix_high: Sparkles,
  badge: BadgeCheck,
  business: Building2,
  cancel: Circle,
  card_giftcard: Gift,
  chat: MessageSquare,
  check_circle: CheckSquare,
  corporate_fare: Building2,
  dashboard: LayoutDashboard,
  dashboard_customize: LayoutDashboard,
  description: FileText,
  dialpad: Phone,
  email: Mail,
  engineering: Wrench,
  event: Calendar,
  event_repeat: CalendarClock,
  extension: Puzzle,
  flight: Plane,
  format_list_bulleted: List,
  graphic_eq: Activity,
  grid_view: Grid2x2,
  groups: Users,
  handshake: Handshake,
  headset_mic: Headset,
  help: HelpCircle,
  help_outline: HelpCircle,
  home: Home,
  inventory: Package,
  inventory_2: Package,
  key: KeyRound,
  list: List,
  list_alt: ClipboardList,
  local_offer: Tag,
  location_city: Hotel,
  luggage: Luggage,
  mail: Mail,
  map: Map,
  meeting_room: Hotel,
  monitor_heart: HeartPulse,
  move_to_inbox: Inbox,
  note: FileText,
  notifications: Bell,
  pan_tool: Handshake,
  password: KeyRound,
  payment: CreditCard,
  payments: CreditCard,
  people: Users,
  person: UserCog,
  person_add: UserPlus,
  person_add_alt: UserPlus,
  pool: Waves,
  price_check: Receipt,
  public: Globe,
  qr_code: QrCode,
  query_stats: TrendingUp,
  real_estate_agent: Home,
  receipt: Receipt,
  receipt_long: Receipt,
  request_quote: FileSpreadsheet,
  route: Route,
  rule: ListChecks,
  school: GraduationCap,
  search: Search,
  security: Shield,
  sell: Tag,
  settings: Settings,
  settings_applications: Settings,
  settings_phone: Phone,
  shopping_cart: ShoppingCart,
  space_dashboard: LayoutDashboard,
  speed: Gauge,
  star: Star,
  star_rate: Star,
  store: Store,
  storefront: Store,
  summarize: FileText,
  support: LifeBuoy,
  support_agent: Headset,
  swap_horizontal_circle: Network,
  table_view: Target,
  today: Calendar,
  trending_up: TrendingUp,
  verified_user: Shield,
  view_kanban: Kanban,
  view_module: AppWindow,
  workspace_premium: Ticket,
  auto_stories: BookOpen,
  smart_toy: Bot,
  pin_drop: MapPin,
  work: Briefcase,
};

/** The Material icon name inside a route's `<Icon>name</Icon>`, if any. */
export function materialName(icon) {
  if (typeof icon === "string") return icon;
  if (isValidElement(icon) && typeof icon.props?.children === "string") {
    return icon.props.children.trim();
  }
  return null;
}

export function lucideFor(icon) {
  const name = materialName(icon);
  return (name && MAP[name]) || null;
}

/** Lucide when we have a mapping, otherwise the route's own icon. */
export function NavIcon({ icon, size = 18 }) {
  const Lucide = lucideFor(icon);
  if (Lucide) return <Lucide size={size} strokeWidth={1.75} aria-hidden="true" />;
  if (isValidElement(icon)) return icon;
  return <Circle size={size} strokeWidth={1.75} aria-hidden="true" />;
}
